import { Activity, Check, History, KeyRound, Loader2, Plus, ShieldOff, Undo2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Section } from '@/components/common/Blocks'
import { Field } from '@/components/common/FormBits'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDateTime } from '../shared/format'
import {
  ACCESS_DURATIONS,
  CRM_ACCESS_APPROVE_PERM,
  useAccessActivity,
  useAccessRequests,
  useCancelAccess,
  useDecideAccess,
  type AccessFilter,
  type AccessRequest,
} from './access'
import { AccessRequestDialog } from './components/AccessRequestDialog'
import { toastError } from './ui'

const FILTERS: AccessFilter[] = ['pending', 'active', 'expired', 'rejected', 'revoked', 'all']

type Decision = { request: AccessRequest; action: 'approve' | 'reject' | 'revoke' }

/** Görünen durum: onaylı talebin süresi dolduysa "süresi doldu". */
function stateOf(r: AccessRequest): { key: string; tone: BadgeTone } {
  if (r.status === 'approved') return r.active ? { key: 'active', tone: 'success' } : { key: 'expired', tone: 'neutral' }
  const tone: Record<string, BadgeTone> = { pending: 'warning', rejected: 'danger', revoked: 'danger', cancelled: 'neutral' }
  return { key: r.status, tone: tone[r.status] ?? 'neutral' }
}

/**
 * Müşteri erişim talepleri. Personel kapsamı dışındaki müşteri için gerekçeli, süreli talep açar; yetkili yönetici
 * onaylar, reddeder ya da süren erişimi geri alır. Erişim süresince yapılan işlemler talep üzerinden izlenir.
 */
export function AccessRequestsPage() {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const isApprover = can(CRM_ACCESS_APPROVE_PERM)
  const [filter, setFilter] = useState<AccessFilter>(isApprover ? 'pending' : 'all')
  const [mine, setMine] = useState(!isApprover)
  const [requesting, setRequesting] = useState(false)
  const [decision, setDecision] = useState<Decision | null>(null)
  const [activityFor, setActivityFor] = useState<AccessRequest | null>(null)
  const list = useAccessRequests(filter, mine)
  const cancel = useCancelAccess()

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.access.title')}
        description={t(isApprover ? 'crm.access.subtitleApprover' : 'crm.access.subtitleOwn')}
        actions={
          <Button onClick={() => setRequesting(true)}>
            <Plus />
            {t('crm.access.new')}
          </Button>
        }
      />
      <Section
        title={t('crm.access.listTitle')}
        icon={KeyRound}
        bodyClassName="p-0"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isApprover ? (
              <Tabs value={mine ? 'mine' : 'everyone'} onValueChange={(v) => setMine(v === 'mine')}>
                <TabsList size="sm">
                  <TabsTrigger value="everyone">{t('crm.access.everyone')}</TabsTrigger>
                  <TabsTrigger value="mine">{t('crm.access.mine')}</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
            <Tabs value={filter} onValueChange={(v) => setFilter(v as AccessFilter)}>
              <TabsList size="sm">
                {FILTERS.map((f) => (
                  <TabsTrigger key={f} value={f}>
                    {t(`crm.access.filter.${f}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        }
      >
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isLoading ? (
          <Skeleton className="m-4 h-40" />
        ) : !list.data?.length ? (
          <EmptyState icon={KeyRound} title={t('crm.access.empty')} />
        ) : (
          <ul className="divide-y">
            {list.data.map((r) => {
              const state = stateOf(r)
              const own = r.requestedBy === user?.id
              return (
                <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-0 flex-1 basis-72 space-y-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {r.active ? (
                        <Link to={`/customers/${r.accountId}`} className="truncate text-primary hover:underline">
                          {r.accountTitle}
                        </Link>
                      ) : (
                        <span className="truncate">{r.accountTitle}</span>
                      )}
                      <Badge tone={state.tone}>{t(`crm.access.status.${state.key}`)}</Badge>
                      <Badge tone="neutral">{t(`crm.access.durations.${r.durationMinutes}`, { defaultValue: `${r.durationMinutes} dk` })}</Badge>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">{t('crm.access.reason')}:</span> {r.reason}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('crm.access.requestedBy', { name: r.requestedByName || '—', at: fmtDateTime(r.createdAt) })}
                      {r.decidedAt ? ` · ${t('crm.access.decidedBy', { name: r.decidedByName || '—', at: fmtDateTime(r.decidedAt) })}` : ''}
                      {r.expiresAt && r.status === 'approved' ? ` · ${t('crm.access.until', { at: fmtDateTime(r.expiresAt) })}` : ''}
                      {r.revokedAt ? ` · ${t('crm.access.revokedAt', { at: fmtDateTime(r.revokedAt) })}` : ''}
                    </p>
                    {r.decisionNote ? <p className="whitespace-pre-line text-xs text-muted-foreground">{t('crm.access.decisionNote')}: {r.decisionNote}</p> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isApprover && r.status === 'pending' && !own ? (
                      <>
                        <Button size="sm" onClick={() => setDecision({ request: r, action: 'approve' })}>
                          <Check />
                          {t('crm.access.approve')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDecision({ request: r, action: 'reject' })}>
                          <X />
                          {t('crm.access.reject')}
                        </Button>
                      </>
                    ) : null}
                    {isApprover && r.active ? (
                      <Button size="sm" variant="outline" onClick={() => setDecision({ request: r, action: 'revoke' })}>
                        <ShieldOff />
                        {t('crm.access.revoke')}
                      </Button>
                    ) : null}
                    {r.startsAt ? (
                      <Button size="sm" variant="ghost" onClick={() => setActivityFor(r)}>
                        <Activity />
                        {t('crm.access.activity')}
                      </Button>
                    ) : null}
                    {own && r.status === 'pending' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(r.id, { onSuccess: () => toast.success(t('crm.access.cancelled')), onError: toastError })}
                      >
                        <Undo2 />
                        {t('crm.access.withdraw')}
                      </Button>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Section>
      {requesting ? <AccessRequestDialog onClose={() => setRequesting(false)} /> : null}
      {decision ? <DecisionDialog decision={decision} onClose={() => setDecision(null)} /> : null}
      {activityFor ? <ActivityDialog request={activityFor} onClose={() => setActivityFor(null)} /> : null}
    </>
  )
}

/** Onay (süre kısaltılabilir, not isteğe bağlı), ret ve geri alma (açıklama zorunlu). */
function DecisionDialog({ decision, onClose }: { decision: Decision; onClose: () => void }) {
  const { t } = useTranslation()
  const decide = useDecideAccess()
  const { request: r, action } = decision
  const [note, setNote] = useState('')
  const [duration, setDuration] = useState(String(r.durationMinutes))
  const [touched, setTouched] = useState(false)
  const noteRequired = action !== 'approve'
  const noteOk = !noteRequired || note.trim().length > 0

  return (
    <Modal
      open
      size="md"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!decide.isPending}
      icon={action === 'approve' ? <Check /> : action === 'reject' ? <X /> : <ShieldOff />}
      title={t(`crm.access.${action}Title`)}
      description={t(`crm.access.${action}Body`, { name: r.requestedByName, customer: r.accountTitle })}
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!noteOk) return
        decide.mutate(
          { id: r.id, action, note, durationMinutes: action === 'approve' ? Number(duration) : undefined },
          {
            onSuccess: () => {
              toast.success(t(`crm.access.${action}Done`))
              onClose()
            },
            onError: toastError,
          },
        )
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant={action === 'approve' ? 'default' : 'destructive'} disabled={decide.isPending}>
            {decide.isPending ? <Loader2 className="animate-spin" /> : null}
            {t(`crm.access.${action}`)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
          <span className="font-medium">{t('crm.access.reason')}:</span> {r.reason}
        </div>
        {action === 'approve' ? (
          <Field id="decision-duration" label={t('crm.access.duration')} hint={t('crm.access.shortenHint')}>
            <Select
              id="decision-duration"
              value={duration}
              onChange={setDuration}
              options={ACCESS_DURATIONS.filter((m) => m <= r.durationMinutes).map((m) => ({ value: String(m), label: t(`crm.access.durations.${m}`) }))}
            />
          </Field>
        ) : null}
        <Field
          id="decision-note"
          label={t(noteRequired ? 'crm.access.noteRequiredLabel' : 'crm.access.noteOptionalLabel')}
          error={touched && !noteOk ? t('crm.access.noteRequired') : undefined}
          full
        >
          <Textarea id="decision-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} aria-invalid={touched && !noteOk} />
        </Field>
      </div>
    </Modal>
  )
}

/** Erişim süresince talep edenin bu müşteriye dokunan istekleri (görüntüleme dahil). */
function ActivityDialog({ request: r, onClose }: { request: AccessRequest; onClose: () => void }) {
  const { t } = useTranslation()
  const activity = useAccessActivity(r.id)
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      icon={<History />}
      title={t('crm.access.activityTitle', { customer: r.accountTitle })}
      description={t('crm.access.activityHint', { name: r.requestedByName })}
      footer={
        <Button type="button" variant="cancel" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {activity.isError ? (
        <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
      ) : activity.isLoading ? (
        <Skeleton className="h-32" />
      ) : !activity.data?.length ? (
        <EmptyState icon={History} title={t('crm.access.activityEmpty')} />
      ) : (
        <ul className="max-h-[60vh] divide-y overflow-y-auto rounded-lg border text-sm">
          {activity.data.map((a, i) => (
            <li key={`${a.at}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <span className="w-36 shrink-0 tabular-nums text-muted-foreground">{fmtDateTime(a.at)}</span>
              <Badge tone={a.method === 'GET' ? 'neutral' : 'info'}>{t(`crm.access.method.${a.method}`, { defaultValue: a.method || a.action })}</Badge>
              <span className="min-w-0 flex-1 truncate font-mono text-xs" title={a.path || a.action}>
                {a.path || a.action}
              </span>
              {a.status >= 400 ? <Badge tone="danger">{a.status}</Badge> : null}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
